import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { TABLE_HEADER_ROW_SX } from '../../../../utils/typographyStyles';
import HeaderProfile from '../../../../components/header_Profile/header_Profile.index';
import InvoicePagination from '../../../../components/Billing/Invoice/InvoicePagination';
import InvoiceSearchBar from '../../../../components/Billing/Invoice/InvoiceSearchBar';
import TableLoader from '../../../../components/common/TableLoader';
import NoDataRow from '../../../../components/common/NoDataRow';
import StatusChip from '../../../../components/Billing/Invoice/InvoiceStatusChip';
import RemarkPopover from '../../../../components/Billing/common/RemarkPopover';
import { usePatientInvoiceLogsQuery, PatientInvoiceLogItem } from './patientInvoiceLogHelper';
import { useClinic } from '../../../../components/ClinicProvider';
import useCurrency from '../../../../components/CurrencyProvider';
import { ReactComponent as PrinterIcon } from '../../../../assets/SVG/printerblue.svg';
import { InvoiceReportGen } from '../../../../components/Reports/Invoice';
import { useInvoiceDetails } from '../newInvoice/newInvoiceForm.helper';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 15, 25, 50];

const LABELS = {
  title: 'Invoice Logs',
  searchPlaceholder: 'Search By Patient Name, Invoice No., Provider',
  noData: 'No invoice logs found.',
  loading: 'Loading...',
};

const ACTION_LABELS = {
  print: 'Print Invoice',
  openRemark: 'Show remark',
};

/** Table header cell config - same as InvoiceTable */
const getHeaderCells = (currencySymbol: string): Array<{
  key: string;
  label: string;
  minWidth?: number;
  align?: 'center' | 'left' | 'right';
  noWrap?: boolean;
}> => [
  { key: 'date', label: 'Date', minWidth: 120, noWrap: true },
  { key: 'billNo', label: 'Bill No.', noWrap: true },
  { key: 'billType', label: 'Bill Type', noWrap: true },
  { key: 'provider', label: 'Provider', noWrap: true },
  { key: 'totalAmt', label: `Total Amt (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'concessionAmt', label: `Concession Amt (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'netAmt', label: `Net Amt (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'paidAmt', label: `Paid Amt (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'balanceAmt', label: `Balance Amt (${currencySymbol})`, align: 'right', noWrap: true },
  { key: 'billRemark', label: 'Bill Remark', noWrap: true },
  { key: 'status', label: 'Status', noWrap: true },
  { key: 'actions', label: 'Actions', align: 'center', noWrap: true },
];

const commonCellSx = {
  verticalAlign: 'middle' as const,
  px: 2.5,
  py: 1.5,
};

// ============================================================================
// Helper: Normalize status
// ============================================================================

const normalizeStatus = (s: string | null | undefined): string => {
  const v = (s || '').toLowerCase();
  switch (v) {
  case 'billed':
  case 'paid':
    return 'Billed';
  case 'settled':
    return 'Billed';
  case 'unsettled':
  case 'unpaid':
  case 'partially_paid':
  case 'partial':
    return 'Unsettle';
  case 'unbilled':
  case 'pending':
    return 'Unbilled';
  case 'draft':
    return 'Draft';
  default:
    return 'Unbilled';
  }
};

// ============================================================================
// Types
// ============================================================================

interface InvoiceRow {
  id: number | null;
  invoiceId: number | null;
  date: string;
  status: string;
  billNo: string;
  billType: string;
  provider: string;
  totalAmt: string;
  concessionAmt: string;
  netAmt: string;
  paidAmt: string;
  balanceAmt: string;
  billRemark: string | null;
}

// ============================================================================
// Component
// ============================================================================

const PatientInvoiceLogs: React.FC = () => {
  const navigate = useNavigate();
  const { id: patientIdFromUrl } = useParams<{ id: string }>();
  const { ClinicId } = useClinic();
  const { currencySymbol } = useCurrency();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Generate header cells with currency symbol
  const headerCells = useMemo(() => getHeaderCells(currencySymbol), [currencySymbol]);

  // Remark popover state
  const [remarkAnchorEl, setRemarkAnchorEl] = useState<null | HTMLElement>(null);
  const [remarkText, setRemarkText] = useState<string>('');

  // Invoice Report dialog state
  const [invoiceReportOpen, setInvoiceReportOpen] = useState(false);
  const [invoiceReportId, setInvoiceReportId] = useState<number | null>(null);

  // Fetch invoice details for invoice report
  const {
    data: invoiceReportDetails,
    isLoading: invoiceReportLoading,
    error: invoiceReportError,
  } = useInvoiceDetails(invoiceReportId ?? undefined);

  // Fetch invoice logs for this patient
  const { data, isLoading } = usePatientInvoiceLogsQuery({
    patientId: patientIdFromUrl ? Number(patientIdFromUrl) : null,
    page,
    limit: pageSize,
    search,
    enabled: !!ClinicId && !!patientIdFromUrl,
  });

  const totalCount = data?.meta?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Map API data to table rows
  const tableRows = useMemo<InvoiceRow[]>(() => {
    if (!data?.objects?.length) return [];
    return data.objects.map((log: PatientInvoiceLogItem) => ({
      id: log.history_id ?? null,
      invoiceId: log.id ?? null,
      date: log.history_date || log.visit_date || '-',
      status: normalizeStatus(log.status || ''),
      billNo: log.bill_number || '-',
      billType: log.bill_type || '-',
      provider: log.provider_name || '-',
      totalAmt: log.total_amount != null ? String(log.total_amount) : '-',
      concessionAmt: log.concession_amount != null ? String(log.concession_amount) : '-',
      netAmt: log.net_amount != null ? String(log.net_amount) : '-',
      paidAmt: log.paid_amount != null ? String(log.paid_amount) : '-',
      balanceAmt: log.balance_amount != null ? String(log.balance_amount) : '-',
      billRemark: log.remark,
    }));
  }, [data]);

  // Handlers
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Remark popover handlers
  const handleOpenRemark = (event: React.MouseEvent<HTMLElement>, text: string) => {
    setRemarkAnchorEl(event.currentTarget);
    setRemarkText(text);
  };

  const handleCloseRemark = () => {
    setRemarkAnchorEl(null);
    setRemarkText('');
  };

  // Invoice report handlers
  const handlePrintInvoice = (invoiceId: number | null) => {
    if (!invoiceId) return;
    setInvoiceReportId(invoiceId);
    setInvoiceReportOpen(true);
  };

  const handleInvoiceReportClose = () => {
    setInvoiceReportOpen(false);
    setInvoiceReportId(null);
  };

  if (isLoading) {
    return <TableLoader minHeight={320} message={LABELS.loading} size={42} thickness={3} />;
  }

  return (
    <>
      <HeaderProfile />
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mx: 2, my: 2 }}>
          <IconButton
            size="small"
            onClick={handleGoBack}
            sx={{ mr: 1.5, borderRadius: 2, border: '1px solid #E5E7EB' }}
            aria-label="Go back"
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1, color: '#111827' }}>
            {LABELS.title}
          </Typography>

          <InvoiceSearchBar
            value={search}
            onChange={handleSearchChange}
            placeholder={LABELS.searchPlaceholder}
          />
        </Box>

        {/* Table */}
        <Box sx={{ mx: 2 }}>
          <TableContainer
            component={Paper}
            sx={{
              minWidth: 360,
              borderRadius: 3,
              boxShadow: '0px 1px 2px rgba(15, 23, 42, 0.06)',
            }}
          >
            <Table
              size="small"
              aria-label="invoice logs table"
              sx={{
                borderCollapse: 'separate',
                borderSpacing: 0,
                '& th, & td': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <TableHead>
                <TableRow sx={TABLE_HEADER_ROW_SX}>
                  {headerCells.map((h, index) => (
                    <TableCell
                      key={h.key}
                      align={h.align}
                      sx={{
                        ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                        ...(h.noWrap ? { whiteSpace: 'nowrap' } : {}),
                        px: 2.5,
                        ...(index === 0 ? { borderTopLeftRadius: 12, borderBottomLeftRadius: 12 } : {}),
                        ...(index === headerCells.length - 1 ? { borderTopRightRadius: 12, borderBottomRightRadius: 12 } : {}),
                      }}
                    >
                      {h.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.length > 0 ? (
                  tableRows.map((row, idx) => {
                    return (
                      <TableRow
                        key={row.id ?? idx}
                        hover
                        sx={{
                          height: 60,
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                        }}
                      >
                        <TableCell sx={{ ...commonCellSx, minWidth: 120, whiteSpace: 'nowrap' }}>
                          {row.date || '-'}
                        </TableCell>
                        <TableCell sx={commonCellSx}>{row.billNo || '-'}</TableCell>
                        <TableCell sx={commonCellSx}>{row.billType || '-'}</TableCell>
                        <TableCell sx={{ ...commonCellSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
                          {row.provider || '-'}
                        </TableCell>
                        <TableCell align="right" sx={commonCellSx}>{row.totalAmt || '-'}</TableCell>
                        <TableCell align="right" sx={commonCellSx}>{row.concessionAmt || '-'}</TableCell>
                        <TableCell align="right" sx={commonCellSx}>{row.netAmt || '-'}</TableCell>
                        <TableCell align="right" sx={commonCellSx}>{row.paidAmt || '-'}</TableCell>
                        <TableCell align="right" sx={commonCellSx}>{row.balanceAmt || '-'}</TableCell>
                        <TableCell sx={commonCellSx}>
                          {row.billRemark && row.billRemark.trim().length > 0 && row.billRemark.trim() !== '-' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 260 }}>
                              <Tooltip title={ACTION_LABELS.openRemark} arrow>
                                <IconButton
                                  size="small"
                                  sx={{ p: 0, color: '#5A8AEA', flexShrink: 0 }}
                                  aria-label={ACTION_LABELS.openRemark}
                                  tabIndex={0}
                                  onClick={(e) => handleOpenRemark(e, row.billRemark || '')}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                                {row.billRemark}
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '30%' }}>-</Box>
                          )}
                        </TableCell>
                        <TableCell sx={commonCellSx}>
                          <StatusChip status={row.status} width={90} height={24} sx={{ fontSize: 12, mt: 0.5 }} />
                        </TableCell>
                        <TableCell align="center" sx={commonCellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, minWidth: 110 }}>
                            <Tooltip title={ACTION_LABELS.print} arrow>
                              <IconButton
                                size="small"
                                aria-label={ACTION_LABELS.print}
                                tabIndex={0}
                                onClick={() => handlePrintInvoice(row.invoiceId)}
                                disabled={!row.invoiceId}
                              >
                                <PrinterIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <NoDataRow message={LABELS.noData} colSpan={headerCells.length} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        <Box sx={{ mx: 2, mt: 2.5 }}>
          <InvoicePagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalEntries={totalCount}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={handlePageSizeChange}
          />
        </Box>

        {/* Remark popover */}
        <RemarkPopover
          anchorEl={remarkAnchorEl}
          open={Boolean(remarkAnchorEl)}
          content={remarkText}
          onClose={handleCloseRemark}
        />

        {/* Invoice Report Dialog */}
        <InvoiceReportGen
          open={invoiceReportOpen}
          onClose={handleInvoiceReportClose}
          data={invoiceReportDetails ?? null}
          isLoading={invoiceReportLoading}
          error={invoiceReportError ? 'Failed to load invoice details.' : null}
        />
      </Box>
    </>
  );
};

export default PatientInvoiceLogs;
